import Box from '@mui/material/Box'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import { alpha } from '@mui/material/styles'
import {
  LAGS,
  REASONS,
  fmtSeconds,
  fmtHours,
  fmtCount,
  durableByClient,
  reasonCounts,
} from './vocabulary'

// The rows as they came back — newest first, the order you read an incident
// in. A 90-day window is 90 rows, so this is the whole dataset rather than a
// sample of it, and it is what an investigation actually reads once the
// charts have said which day to look at.
//
// Lag columns follow the page's percentile toggle instead of showing all
// three: twelve numeric columns is not a table anyone reads.
const DayTable = ({
  days,
  percentile,
  percentileLabel,
  selectedDay,
  onSelectDay,
}) => {
  if (!days.length) {
    return (
      <Typography variant="body2" color="text.secondary">
        No spans declared in this window.
      </Typography>
    )
  }

  return (
    <Box sx={{ overflowX: 'auto' }}>
      <Table size="small" sx={{ minWidth: 860 }}>
        <TableHead>
          <TableRow>
            <TableCell>Day (UTC)</TableCell>
            <TableCell align="right">Declared</TableCell>
            <TableCell align="right">Durable</TableCell>
            <TableCell align="right">
              <Tooltip title="Arrived in full, but the phone never confirmed it, so the sweep did. Left out of the times.">
                <span>By sweep</span>
              </Tooltip>
            </TableCell>
            <TableCell align="right">Never durable</TableCell>
            <TableCell align="right">
              <Tooltip title="Declared audio on spans with no seal stamp — the loss counter.">
                <span>Unsealed</span>
              </Tooltip>
            </TableCell>
            {LAGS.map(({ key, label, help }) => (
              <TableCell key={key} align="right">
                <Tooltip title={help}>
                  <span>
                    {label}
                    <Typography
                      component="span"
                      variant="caption"
                      color="text.secondary"
                      sx={{ ml: 0.5 }}
                    >
                      {percentileLabel}
                    </Typography>
                  </span>
                </Tooltip>
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {days.map((row) => {
            const { counts, transport } = row
            const reasons = reasonCounts(counts)
            const selected = row.day === selectedDay
            return (
              <TableRow
                key={row.day}
                hover
                onClick={() => onSelectDay?.(row.day)}
                sx={{
                  cursor: 'pointer',
                  bgcolor: selected
                    ? (theme) => alpha(theme.palette.primary.main, 0.12)
                    : undefined,
                }}
              >
                <TableCell>{row.day}</TableCell>
                <TableCell align="right">{fmtCount(counts.declared)}</TableCell>
                <TableCell align="right">
                  {fmtCount(durableByClient(counts))}
                </TableCell>
                <TableCell align="right">
                  {fmtCount(counts.durable_by_sweep)}
                </TableCell>
                <TableCell align="right">
                  {counts.never_durable > 0 ? (
                    <Tooltip
                      title={REASONS.filter(({ key }) => reasons[key])
                        .map(({ key, label }) => `${label}: ${reasons[key]}`)
                        .join(' · ')}
                    >
                      <Box
                        component="span"
                        sx={{
                          display: 'inline-flex',
                          gap: 0.5,
                          alignItems: 'center',
                        }}
                      >
                        {REASONS.filter(({ key }) => reasons[key]).map(
                          ({ key, color }) => (
                            <Box
                              key={key}
                              sx={{
                                width: 7,
                                height: 7,
                                borderRadius: '50%',
                                bgcolor: color,
                              }}
                            />
                          )
                        )}
                        {fmtCount(counts.never_durable)}
                      </Box>
                    </Tooltip>
                  ) : (
                    fmtCount(0)
                  )}
                </TableCell>
                <TableCell align="right">
                  {fmtHours(counts.unsealed_hours)}
                </TableCell>
                {LAGS.map(({ key }) => (
                  <TableCell key={key} align="right">
                    {fmtSeconds(transport[key][percentile])}
                  </TableCell>
                ))}
              </TableRow>
            )
          })}
        </TableBody>
      </Table>
    </Box>
  )
}

export default DayTable
